import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import BSCAuthLayout from '../components/BSCAuthLayout';
import { toast } from 'sonner';
import { AlertTriangle, KeyRound, UserPlus, Home } from 'lucide-react';
import { isNativeApp, saveTokens } from '../utils/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const { login, checkAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BSCAuthLayout wordmark="SIGN IN">
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-white/70 text-xs tracking-[0.2em] uppercase">Email</Label>
          <Input id="email" data-testid="login-email-input" type="email"
            value={email} onChange={(e) => setEmail(e.target.value)} required
            className="mt-2 bg-black/30 border-white/15 text-white placeholder:text-white/30 focus:border-white/40" />
        </div>
        <div>
          <Label htmlFor="password" className="text-white/70 text-xs tracking-[0.2em] uppercase">Password</Label>
          <Input id="password" data-testid="login-password-input" type="password"
            value={password} onChange={(e) => setPassword(e.target.value)} required
            className="mt-2 bg-black/30 border-white/15 text-white placeholder:text-white/30 focus:border-white/40" />
        </div>
        {error && (
          <div className="text-sm p-3 rounded border"
            style={{ backgroundColor: 'rgba(242,140,40,0.08)', color: '#F28C28', borderColor: 'rgba(242,140,40,0.3)' }}
            data-testid="login-error-message">
            {error}
          </div>
        )}
        <Button type="submit" data-testid="login-submit-button" disabled={loading}
          className="w-full tracking-[0.25em] uppercase text-xs font-semibold bg-white text-black hover:bg-white/90">
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <div className="mt-6 flex flex-col gap-2 items-center">
        <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-btn"
          className="text-white/60 hover:text-white text-[11px] tracking-widest uppercase flex items-center gap-1">
          <KeyRound size={12} /> Forgot password?
        </button>
        <button type="button" onClick={() => setCreateOpen(true)} data-testid="create-admin-btn"
          className="text-white/60 hover:text-white text-[11px] tracking-widest uppercase flex items-center gap-1">
          <UserPlus size={12} /> Create new admin account
        </button>
        <button type="button" onClick={() => navigate('/')} data-testid="back-to-home-btn"
          className="mt-3 text-white/50 hover:text-white text-[11px] tracking-widest uppercase flex items-center gap-1">
          <Home size={12} /> Back to home
        </button>
      </div>

      <div className="mt-4 text-center text-xs tracking-widest uppercase text-white/40">
        BSC Business OS
      </div>

      <CreateAdminDialog open={createOpen} onOpenChange={setCreateOpen}
        onCreated={async () => { setCreateOpen(false); await checkAuth(); navigate(next); }} />
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </BSCAuthLayout>
  );
};

function CreateAdminDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', password: '' });
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('details'); // 'details' | 'otp'
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otpRecipient, setOtpRecipient] = useState('');

  const sendOtp = async () => {
    if (!form.email || !form.name.trim() || !form.password) {
      toast.error('All fields are required'); return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    if (!confirmed) {
      toast.error('Please confirm you understand the security warning'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/request-admin-otp`, form);
      // Extract recipient from message ("Verification code sent to X. ...")
      const m = (r.data?.message || '').match(/sent to ([\w.+-]+@[\w.-]+)/i);
      setOtpRecipient(m ? m[1] : 'the authoriser email');
      toast.success(r.data?.message || 'OTP sent');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    } finally { setSubmitting(false); }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Enter the 6-digit code from the email'); return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-admin-otp`, { ...form, otp }, { withCredentials: true });
      // On mobile, persist tokens so the user is signed in across requests.
      if (isNativeApp() && data?.access_token) {
        await saveTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
      }
      toast.success('Admin account created — you are signed in');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setSubmitting(false); }
  };

  const reset = () => { setForm({ email: '', name: '', password: '' }); setOtp(''); setStep('details'); setConfirmed(false); };
  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="create-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} /> Create new admin account
          </DialogTitle>
          <DialogDescription>
            {step === 'details'
              ? 'A 6-digit code will be emailed to the authoriser. Enter the code on the next step to confirm.'
              : `A 6-digit code was sent to ${otpRecipient}. Enter it below.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'details' && (
          <>
            <div className="rounded-lg p-3 border flex gap-2 items-start"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)', borderColor: 'rgba(220, 38, 38, 0.4)' }}>
              <AlertTriangle size={18} className="shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
              <div className="text-xs" style={{ color: '#7F1D1D' }}>
                <b>OTP-protected</b> — even though this endpoint is public, an OTP is sent to the
                authoriser email (set in env as <code>ADMIN_OTP_RECIPIENT</code>). Only someone with
                access to that inbox can complete the signup.
              </div>
            </div>

            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Email (new admin)</Label>
                <Input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="create-admin-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Name</Label>
                <Input value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="create-admin-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Password (min 6 chars)</Label>
                <Input type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="create-admin-password" />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="create-admin-confirm" />
                I understand this account has full system access.
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={sendOtp} disabled={submitting} data-testid="create-admin-send-otp"
                style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
                {submitting ? 'Sending…' : 'Send verification code'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'otp' && (
          <>
            <div className="rounded-lg p-3 border text-xs"
              style={{ backgroundColor: '#FAF7F2', borderColor: '#E8E3DB' }}>
              The code expires in 10 minutes. Wrong code 5 times = blocked, request a new one.
            </div>
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-widest">6-digit code</Label>
              <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="text-center tracking-[0.5em] font-mono text-lg"
                data-testid="create-admin-otp" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('details')}>Back</Button>
              <Button onClick={verifyOtp} disabled={submitting || otp.length < 6} data-testid="create-admin-verify-otp"
                style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
                {submitting ? 'Verifying…' : 'Verify & create admin'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ForgotPasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ email: '', setup_token: '', new_password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.email || !form.setup_token || !form.new_password) {
      toast.error('All fields are required'); return;
    }
    if (form.new_password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, form);
      setDone(true);
      toast.success('Password reset — sign in with the new password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setSubmitting(false); }
  };

  const close = () => { setDone(false); setForm({ email: '', setup_token: '', new_password: '' }); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} /> Reset password
          </DialogTitle>
          <DialogDescription>
            Enter your email, the setup token from the system owner, and your new password.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="rounded-lg p-3 border text-sm"
            style={{ backgroundColor: 'rgba(26,168,75,0.08)', borderColor: 'rgba(26,168,75,0.4)', color: '#14532D' }}>
            ✓ Password reset successfully. Close this dialog and sign in with the new password.
          </div>
        ) : (
          <>
            <div className="rounded-lg p-3 border flex gap-2 items-start text-xs"
              style={{ backgroundColor: '#FAF7F2', borderColor: '#E8E3DB' }}>
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-black/50" />
              <div className="text-black/70">
                The system owner must set <code className="px-1 rounded bg-black/10">ADMIN_SETUP_TOKEN</code> in the backend
                <code> .env</code> file before this works. Ask them for the current token value.
              </div>
            </div>

            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="forgot-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Setup token</Label>
                <Input type="password" value={form.setup_token}
                  onChange={(e) => setForm({ ...form, setup_token: e.target.value })}
                  data-testid="forgot-token" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">New password (min 6 chars)</Label>
                <Input type="password" value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  data-testid="forgot-new-password" />
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>Close</Button>
          {!done && (
            <Button onClick={submit} disabled={submitting} data-testid="forgot-submit"
              style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
              {submitting ? 'Resetting…' : 'Reset password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default Login;
